// NEW part categories (Barrels, Chassis, Uppers, Sights, Triggers) — flat list for now, but every
// leaf carries a STABLE machine ID that is stored on the listing (never the display label). When the
// two-level parent→leaf tree is built later this becomes a mapping table, not a data migration.
//
// NOTE: existing optic categories still store their DISPLAY LABEL on the listing (legacy). These new
// leaves are ID-first so nothing new gets orphaned.

export const PART_GROUPS = ["barrels", "muzzle_devices", "chassis", "uppers", "sights", "triggers", "handguards", "magazines", "ammunition", "drones"];

// TOP-LEVEL parent categories — peers of the optic categories. The parent is what the seller/buyer
// sees & filters by; the LEAF machine id (barrels.rifle) is what's stored on the listing.
export const PART_PARENTS = [
  { id: "barrels", label: "Barrels" },
  { id: "muzzle_devices", label: "Muzzle Devices" },
  { id: "chassis", label: "Chassis" },
  { id: "uppers", label: "Uppers" },
  { id: "sights", label: "Sights" },
  { id: "triggers", label: "Triggers" },
  { id: "handguards", label: "Handguards" },
  { id: "magazines", label: "Magazines" },
  { id: "ammunition", label: "Ammunition" },
  { id: "drones", label: "Drones" },
];
export const PART_PARENT_LABELS = PART_PARENTS.map((p) => p.label);
const _LABEL_TO_GROUP = Object.fromEntries(PART_PARENTS.map((p) => [p.label, p.id]));
const _GROUP_TO_LABEL = Object.fromEntries(PART_PARENTS.map((p) => [p.id, p.label]));
export const labelToGroup = (label) => _LABEL_TO_GROUP[label] || null;
export const isPartParent = (label) => !!_LABEL_TO_GROUP[label];
export const parentLabelForLeaf = (leafId) => _GROUP_TO_LABEL[partGroupOf(leafId)] || null;
// Bare part-GROUP id (e.g. "muzzle_devices") -> parent display label ("Muzzle Devices"). Null if the
// id isn't a known part group. Used by catalog prefill when the resolver returns a bare group id.
export const groupToParentLabel = (groupId) => _GROUP_TO_LABEL[groupId] || null;

// Leaf id → display label. Parent word first so they sort together in the flat <select>.
export const PART_LEAVES = [
  { id: "barrels.rifle", label: "Barrels — Rifle", group: "barrels" },
  { id: "barrels.pistol", label: "Barrels — Pistol", group: "barrels" },
  { id: "barrels.shotgun", label: "Barrels — Shotgun", group: "barrels" },
  { id: "barrels.prefit", label: "Barrels — Pre-fit", group: "barrels" },
  { id: "barrels.blank", label: "Barrels — Blanks", group: "barrels" },

  { id: "muzzle_devices.brake", label: "Muzzle Devices — Brake", group: "muzzle_devices" },
  { id: "muzzle_devices.flash_hider", label: "Muzzle Devices — Flash Hider", group: "muzzle_devices" },
  { id: "muzzle_devices.adapter", label: "Muzzle Devices — Adapter", group: "muzzle_devices" },
  { id: "muzzle_devices.mount", label: "Muzzle Devices — Mount", group: "muzzle_devices" },
  { id: "muzzle_devices.front_cap", label: "Muzzle Devices — Front Cap", group: "muzzle_devices" },
  { id: "muzzle_devices.piston", label: "Muzzle Devices — Piston", group: "muzzle_devices" },
  { id: "muzzle_devices.thread_protector", label: "Muzzle Devices — Thread Protector", group: "muzzle_devices" },
  { id: "muzzle_devices.thread_spacer", label: "Muzzle Devices — Thread Spacer", group: "muzzle_devices" },
  { id: "muzzle_devices.thread_insert", label: "Muzzle Devices — Thread Insert", group: "muzzle_devices" },
  { id: "muzzle_devices.tool", label: "Muzzle Devices — Tool", group: "muzzle_devices" },

  { id: "chassis.system", label: "Chassis — Systems", group: "chassis" },
  { id: "chassis.stock", label: "Chassis — Stocks", group: "chassis" },
  { id: "chassis.bottom_metal", label: "Chassis — Bottom metal", group: "chassis" },

  { id: "uppers.complete", label: "Uppers — Complete", group: "uppers" },
  { id: "uppers.stripped", label: "Uppers — Stripped", group: "uppers" },
  { id: "uppers.handguard", label: "Uppers — Handguards / rails", group: "uppers" },

  { id: "sights.iron", label: "Sights — Iron", group: "sights" },
  { id: "sights.backup", label: "Sights — Backup", group: "sights" },
  { id: "sights.front", label: "Sights — Front", group: "sights" },
  { id: "sights.rear", label: "Sights — Rear", group: "sights" },
  { id: "sights.set", label: "Sights — Sets", group: "sights" },

  { id: "triggers.rifle", label: "Triggers — Rifle", group: "triggers" },
  { id: "triggers.ar", label: "Triggers — AR", group: "triggers" },
  { id: "triggers.shotgun", label: "Triggers — Shotgun", group: "triggers" },
  { id: "triggers.parts", label: "Triggers — Parts", group: "triggers" },

  { id: "handguards.mlok", label: "Handguards — M-LOK", group: "handguards" },
  { id: "handguards.keymod", label: "Handguards — KeyMod", group: "handguards" },
  { id: "handguards.quad", label: "Handguards — Quad rail (Picatinny)", group: "handguards" },
  { id: "handguards.other", label: "Handguards — Other / proprietary", group: "handguards" },

  { id: "magazines.rifle", label: "Magazines — Rifle", group: "magazines" },
  { id: "magazines.pistol", label: "Magazines — Pistol", group: "magazines" },
  { id: "magazines.rimfire", label: "Magazines — Rimfire", group: "magazines" },
  { id: "magazines.drum", label: "Magazines — Drum", group: "magazines" },

  { id: "ammunition.rifle", label: "Ammunition — Rifle", group: "ammunition" },
  { id: "ammunition.pistol", label: "Ammunition — Handgun", group: "ammunition" },
  { id: "ammunition.rimfire", label: "Ammunition — Rimfire", group: "ammunition" },
  { id: "ammunition.shotgun", label: "Ammunition — Shot Shell", group: "ammunition" },
  { id: "ammunition.components", label: "Ammunition — Components (brass, bullets, primers, powder)", group: "ammunition" },

  { id: "drones.consumer", label: "Drones — Consumer", group: "drones" },
  { id: "drones.enterprise", label: "Drones — Enterprise / Commercial", group: "drones" },
  { id: "drones.agricultural", label: "Drones — Agricultural (Agras)", group: "drones" },
  { id: "drones.delivery", label: "Drones — Delivery / Cargo", group: "drones" },
  { id: "drones.parts", label: "Drones — Parts & Accessories", group: "drones" },
];

const _LEAF_BY_ID = Object.fromEntries(PART_LEAVES.map((l) => [l.id, l]));
export const isPartCategory = (cat) => !!_LEAF_BY_ID[cat];
export const partGroupOf = (cat) => _LEAF_BY_ID[cat]?.group || null;
export const partLeafLabel = (cat) => _LEAF_BY_ID[cat]?.label || cat;

// Groups subject to the serialized-firearm moderation (mirror of backend part_categories.py).
// Muzzle devices, magazines, and drones are NOT firearm receivers, so they skip that detector —
// e.g. a "lower capacity" magazine listing must not be false-flagged on the word "lower".
export const FIREARM_PART_GROUPS = new Set(["barrels", "chassis", "uppers", "sights", "triggers", "handguards"]);
export const isFirearmPartCategory = (cat) => FIREARM_PART_GROUPS.has(partGroupOf(cat));

// Ammunition is its own part group. Ships on UPS/FedEx Ground only (USPS won't carry ammo) and
// requires a DOT Limited Quantity hazmat marking on the box — enforced/surfaced in the ship flow.
export const isAmmunitionCategory = (cat) => partGroupOf(cat) === "ammunition";

// Leaves under a parent label, with a SHORT label (parent word stripped) for the 2nd-level select.
export const leavesForParentLabel = (label) => {
  const g = labelToGroup(label);
  return PART_LEAVES.filter((l) => l.group === g).map((l) => ({ ...l, short: l.label.split(" — ")[1] || l.label }));
};

// Groups for the <select> optgroups.
export const PART_OPTGROUPS = PART_GROUPS.map((g) => ({
  group: g,
  label: g.charAt(0).toUpperCase() + g.slice(1),
  leaves: PART_LEAVES.filter((l) => l.group === g),
}));

// ============================================================================
// AMMUNITION TAXONOMY — RSR Group sweep (2,544-SKU sample, Sep 2026)
// Caliber options are GROUPED BY SUBCATEGORY (rifle / handgun / rimfire / shotgun) and the listing
// form only shows the calibers for the chosen subcategory — never one flat 164-item list. Grain
// Weight (numeric, gr) applies to rifle/handgun/rimfire; Shot Shell uses a Shot Size dropdown plus
// shell-only optional fields (Dram, Ounce of shot, CA Certified Nonlead). Bullet/load type is an
// optional autocomplete (free text allowed) and Manufacturer is an autocomplete (39 makers + Other).
// ============================================================================
export const AMMO_CALIBERS = {
  // Shotgun gauges (18)
  "ammunition.shotgun": [
    "10 Gauge", "10 Gauge 3.5\"", "12 Gauge", "12 Gauge 1.75\"", "12 Gauge 2.75\"", "12 Gauge 3\"",
    "12 Gauge 3.5\"", "16 Gauge", "16 Gauge 2.75\"", "20 Gauge", "20 Gauge 2.75\"", "20 Gauge 3\"",
    "28 Gauge", "28 Gauge 2.75\"", "28 Gauge 3\"", "410 Bore", "410 Bore 2.5\"", "410 Bore 3\"",
  ],
  // Rimfire (10)
  "ammunition.rimfire": [
    "17 HMR", "17 Hornady Mach 2", "17 Hornet", "17 Remington FireBall", "17 Winchester Super Magnum",
    "22 Long", "22 LR", "22 Short", "22 WMR", "22 WRF",
  ],
  // Handgun (34)
  "ammunition.pistol": [
    "10MM", "22 TCM", "25 ACP", "30 Carbine", "30 Super Carry", "32 ACP", "32 H&R", "32 S&W Long",
    "32 S&W", "327 Federal Magnum", "357 Magnum", "357 Sig", "38 Special", "38 Super", "380 ACP",
    "4.6X30MM", "40 S&W", "41 Magnum", "44 Magnum", "44 Special", "44-40 Winchester", "45 ACP",
    "45 Colt", "45 GAP", "454 Casull", "460 S&W", "480 Ruger", "50 Action Express", "500 S&W",
    "5.7X28MM", "7.62X25 Tokarev", "9MM Makarov", "9MM", "9X23",
  ],
  // Rifle (102)
  "ammunition.rifle": [
    "204 Ruger", "21 SHARP", "218 Bee", "22 ARC", "22 Creedmoor", "22 Hornet", "22 Nosler",
    "22 Winchester Automatic", "22-250 Remington", "220 Swift", "222 Remington", "223 Remington",
    "223 Winchester Super Short Magnum", "224 Valkyrie", "240 Weatherby Magnum",
    "243 Winchester Super Short Magnum", "243 Winchester", "25 Creedmoor",
    "25 Winchester Super Short Magnum", "25-06 Remington", "25-20 Winchester", "25-35 Winchester",
    "250 Savage", "257 Roberts", "257 Weatherby Magnum", "26 Nosler", "260 Remington",
    "264 Winchester", "270 Weatherby Magnum", "270 Winchester Short Magnum", "270 Winchester",
    "277 Fury", "28 Nosler", "280 Ackley Improved", "280 Remington", "30 Nosler", "30-06 Springfield",
    "30-30 Winchester", "30-40 Krag", "300 Blackout", "300 PRC", "300 Remington Short Action Ultra Mag",
    "300 Remington Ultra Magnum", "300 Savage", "300 Weatherby Magnum", "300 Winchester Magnum",
    "300 Winchester Short Magnum", "303 British", "308 Marlin Express", "308 Winchester",
    "32 Winchester Special", "32-20 Winchester", "33 Nosler", "338 ARC", "338 Lapua",
    "338 Marlin Express", "338 Remington Ultra Magnum", "338 Winchester", "35 Remington", "35 Whelen",
    "350 Legend", "360 Buckhammer", "375 H&H", "375 Ruger", "375 Winchester", "400 Legend",
    "416 Barrett", "444 Marlin", "45-70 Government", "450 Bushmaster", "450 Marlin", "458 Socom",
    "458 Winchester", "5.45X39", "50 BMG", "556NATO", "6.5 Creedmoor", "6.5 Grendel", "6.5 PRC",
    "6.5-300 Weatherby Magnum", "6.5X55 Swedish", "6.8 SPC", "6.8 Western", "6MM ARC", "6MM Creedmoor",
    "6MM GT", "6MM Remington", "7.5 FK", "7.5x55mm Swiss", "7.62X39", "7.62X54R", "762NATO",
    "7MM Backcountry", "7MM PRC", "7MM Remington Magnum", "7MM Short Action Ultra Mag",
    "7MM Weatherby Magnum", "7MM WSM", "7MM-08", "7X57", "8.6 Blackout", "8MM Mauser",
  ],
};
const AMMO_CALIBER_UNION = [...new Set(Object.values(AMMO_CALIBERS).flat())];

// Shot Shell — Shot Size dropdown (18)
export const AMMO_SHOT_SIZES = [
  "#12", "#9", "#8.5", "#8", "#7.5", "#7", "#6", "#5", "#4", "#3", "#2",
  "BB", "1 Buck", "2 Buck", "3 Buck", "4 Buck", "000 Buck", "00 Buck",
];
// Shot Shell — Dram (7)
export const AMMO_DRAMS = ["2 1/2", "2 3/4", "3", "3 1/4", "3 3/4", "4", "Max"];
// Shot Shell — Ounce of shot (autocomplete, 1/4 oz → 2 1/4 oz; free text allowed)
export const AMMO_OZ_SHOT = [
  "1/4 oz", "3/8 oz", "1/2 oz", "5/8 oz", "11/16 oz", "3/4 oz", "7/8 oz", "15/16 oz", "1 oz",
  "1 1/8 oz", "1 1/4 oz", "1 3/8 oz", "1 1/2 oz", "1 5/8 oz", "1 3/4 oz", "1 7/8 oz", "2 oz", "2 1/4 oz",
];
// Bullet / load type — OPTIONAL secondary filter (autocomplete, free text allowed for the ~80 others)
export const AMMO_LOAD_TYPES = [
  "Full Metal Jacket", "Hollow Point", "Soft Point", "Ballistic Tip", "Boat Tail Hollow Point",
  "Jacketed Soft Point", "Polymer Tip", "V-Max", "ELD-X", "ELD Match", "AccuBond", "Partition",
  "Copper", "Buckshot", "Steel Shot", "Rifled Slug", "Sabot Slug", "TSS", "Tungsten", "Shotshell",
  "Shot", "Frangible", "Solid Base", "Round Nose", "Wadcutter", "Total Metal Jacket",
];
// Manufacturer — autocomplete seed (39 makers + Other free text)
export const AMMO_MANUFACTURERS = [
  "ADI World Class Ammunition", "Aguila Ammunition", "Armscor", "Barnes", "Barrett", "Belom",
  "Berger Bullets", "Blazer Ammunition", "CCI Ammunition", "Century Arms", "Corbon Ammo",
  "DoubleTap Ammunition", "Federal", "Fiocchi Ammunition", "FK BRNO", "FN America",
  "Frontier Cartridge", "G2 Research", "Global Ordnance", "HEVI-Shot", "Hornady", "Lapua",
  "Lehigh Defense", "Magnum Research", "Magtech", "Maxim Defense Industries", "Nosler",
  "PMC Ammunition", "Prvi Partizan", "Remington", "Sellier & Bellot", "Sig Sauer",
  "Speer Ammunition", "Stars and Stripes Defense Ammunition", "Sterling", "STV Technology",
  "Umarex", "Weatherby", "Winchester Ammunition", "Other",
];

const AMMO_MAKEUP = ["Factory new", "Remanufactured", "Handload"];

// Leaf-aware ammunition spec fields. The caliber dropdown is scoped to the chosen subcategory;
// Shot Shell swaps Grain Weight for Shot Size and adds the shell-only optional fields.
export function ammoFieldsForLeaf(leafId) {
  const isShot = leafId === "ammunition.shotgun";
  const cal = AMMO_CALIBERS[leafId] || AMMO_CALIBER_UNION;  // components → union of all calibers
  const fields = [
    { key: "caliber", label: isShot ? "Gauge / bore" : "Caliber / cartridge", type: "select", required: true, options: cal },
    { key: "quantity", label: "Quantity (rounds)", type: "number", hint: "e.g. 20, 50, 1000" },
  ];
  if (isShot) {
    fields.push(
      { key: "shot_size", label: "Shot size", type: "select", options: AMMO_SHOT_SIZES },
      { key: "dram", label: "Dram (optional)", type: "select", options: AMMO_DRAMS },
      { key: "oz_shot", label: "Ounce of shot (optional)", type: "suggest", suggest: AMMO_OZ_SHOT, hint: "e.g. 1 1/8 oz" },
      { key: "ca_nonlead", label: "CA Certified Nonlead", type: "bool" },
    );
  } else {
    fields.push({ key: "grain", label: "Grain weight (gr)", type: "number", hint: "e.g. 55, 124, 147 — typical 15–750 gr" });
  }
  fields.push(
    { key: "bullet_type", label: "Bullet / load type (optional)", type: "suggest", suggest: AMMO_LOAD_TYPES, hint: "Optional — e.g. Full Metal Jacket, Hollow Point, Buckshot" },
    { key: "makeup", label: "Make-up", type: "select", options: AMMO_MAKEUP },
  );
  return fields;
}

// ---- Per-group structured spec fields ----
// type: text | number | select | bool | suggest
// R = required. showIf(specs) gates conditional fields (barrel gas system only for AR fits).
// topRequired: existing top-level form fields (brand/model) that must be filled for this group.
const AR_FITS = ["AR-15 ext", "AR-10 ext"];

export const PART_SPECS = {
  barrels: {
    topRequired: [],
    fields: [
      { key: "chambering", label: "Chambering / caliber", type: "suggest", required: true, suggest: ["6mm ARC", "6.5 Creedmoor", ".308 Win", "6.5 PRC", "6 GT", ".223 Rem", "300 PRC", "22 ARC", "6.5x47 Lapua"] },
      { key: "barrel_length_in", label: "Barrel length (in)", type: "number", required: true },
      { key: "action_fit", label: "Action fit / shank", type: "select", required: true, options: ["Rem 700", "Savage small shank", "Savage large shank", "AR-15 ext", "AR-10 ext", "Tikka", "Other"] },
      { key: "gas_system", label: "Gas system length", type: "select", required: true, options: ["pistol", "carbine", "mid", "rifle", "N/A"], showIf: (s) => AR_FITS.includes(s.action_fit) },
      { key: "gas_port_diameter", label: "Gas port diameter (in)", type: "text", hint: "e.g. 0.086", showIf: (s) => AR_FITS.includes(s.action_fit) },
      { key: "twist_rate", label: "Twist rate", type: "text", required: true, hint: "e.g. 1:8 or 1:7.5" },
      { key: "contour", label: "Contour / profile", type: "text", hint: "Heavy Palma, M24, Sendero, pencil" },
      { key: "material_finish", label: "Material / finish", type: "text", hint: "stainless, chrome moly, nitride, Cerakote" },
      { key: "thread_pitch", label: "Thread pitch", type: "select", options: ["5/8x24", "1/2x28", "None"] },
      { key: "fluted", label: "Fluted", type: "bool" },
      { key: "round_count", label: "Round count (optional)", type: "number" },
    ],
  },
  muzzle_devices: {
    topRequired: [],
    fields: [
      { key: "fit", label: "Fit (thread pitch)", type: "suggest", hint: "e.g. 1/2x28, 5/8x24 — or type your own", suggest: ["1/2x28", "5/8x24", "M13.5x1 LH", "M14x1 LH", "M15x1", "M18x1", "9/16x24", "11/16x24", "3/4x24", "1/2x36", ".578x28"] },
    ],
  },
  chassis: {
    topRequired: ["brand", "model"],
    fields: [
      { key: "action_inlet", label: "Action inlet", type: "select", required: true, options: ["Rem 700", "Tikka", "Savage", "AR", "Other"] },
      { key: "fore_end_type", label: "Fore-end type", type: "text" },
      { key: "length_of_pull", label: "Length of pull", type: "text" },
      { key: "adjustable_cheek", label: "Adjustable cheek", type: "bool" },
      { key: "magazine_compat", label: "Magazine compatibility", type: "text", hint: "AICS etc." },
      { key: "color_finish", label: "Color / finish", type: "text" },
      { key: "weight", label: "Weight", type: "text" },
    ],
  },
  uppers: {
    topRequired: [],
    attestation: "I understand a complete upper under 16 inches assembled onto a rifle lower creates a short-barreled rifle, and that some states restrict specific upper configurations. I am responsible for confirming this item is legal to ship to the buyer's state.",
    fields: [
      { key: "chambering", label: "Chambering", type: "suggest", required: true, suggest: ["5.56 NATO", ".223 Wylde", "300 BLK", "6mm ARC", "6.5 Grendel", "224 Valkyrie", ".308 Win"] },
      { key: "barrel_length_in", label: "Barrel length (in)", type: "number", required: true },
      { key: "gas_system", label: "Gas system length", type: "select", required: true, options: ["pistol", "carbine", "mid", "rifle"] },
      { key: "handguard", label: "Handguard brand + length", type: "text" },
      { key: "bcg_included", label: "Bolt carrier group included", type: "bool" },
      { key: "charging_handle_included", label: "Charging handle included", type: "bool" },
      { key: "muzzle_device", label: "Muzzle device", type: "text" },
    ],
  },
  sights: {
    topRequired: ["brand", "model"],
    fields: [
      { key: "sight_type", label: "Type", type: "select", required: true, options: ["fixed", "folding", "flip-up"] },
      { key: "mount_interface", label: "Mount interface", type: "select", required: true, options: ["picatinny", "dovetail", "integral", "other"] },
      { key: "front_or_rear", label: "Front or rear", type: "select", required: true, options: ["front", "rear", "set"] },
      { key: "height", label: "Height", type: "text" },
      { key: "tritium", label: "Tritium", type: "bool" },
    ],
  },
  triggers: {
    topRequired: ["brand", "model"],
    fields: [
      { key: "action_fit", label: "Action fit", type: "select", required: true, options: ["Rem 700", "AR-15", "AR-10", "Tikka", "Savage", "Other"] },
      { key: "stage", label: "Single or two-stage", type: "select", required: true, options: ["single", "two-stage"] },
      { key: "pull_weight_lb", label: "Pull weight (lb)", type: "number" },
      { key: "adjustable", label: "Adjustable", type: "bool" },
      { key: "safety_type", label: "Safety type", type: "text" },
    ],
  },
  handguards: {
    topRequired: ["brand", "model"],
    fields: [
      { key: "length_in", label: "Length (in)", type: "number", required: true, hint: "e.g. 7.39" },
      { key: "mounting_system", label: "Mounting / attachment system", type: "select", required: true, options: ["M-LOK", "KeyMod", "Quad rail (Picatinny)", "Picatinny + M-LOK", "Proprietary", "Other"] },
      { key: "compatible_platform", label: "Compatible platform", type: "select", required: true, options: ["AR-15", "AR-10 / LR-308", "AK", "Remington 700", "Ruger 10/22", "Other"] },
      { key: "material", label: "Material", type: "text", hint: "e.g. 6061-T6 aluminum" },
      { key: "weight_oz", label: "Weight (oz)", type: "number", hint: "handguard only" },
      { key: "free_float", label: "Free-float design", type: "bool" },
      { key: "barrel_nut", label: "Barrel nut / mounting", type: "text", hint: "e.g. proprietary steel barrel nut" },
      { key: "finish", label: "Finish", type: "text", hint: "e.g. hard coat anodized, Cerakote" },
    ],
  },
  magazines: {
    topRequired: [],
    fields: [
      { key: "capacity", label: "Capacity (rounds)", type: "number", hint: "e.g. 10, 30" },
      { key: "caliber", label: "Caliber / cartridge", type: "text", hint: "e.g. 5.56 NATO, 9mm, .308 Win" },
    ],
  },
  ammunition: {
    // Leaf-aware at render time via ammoFieldsForLeaf() / visibleFieldsFor("ammunition", specs, leaf).
    // This static list is the SUPERSET of every possible key (union caliber options) so edit/catalog
    // prefill keeps working regardless of which subcategory a saved listing used.
    topRequired: [],
    fields: [
      { key: "caliber", label: "Caliber / cartridge", type: "select", required: true, options: AMMO_CALIBER_UNION },
      { key: "quantity", label: "Quantity (rounds)", type: "number", hint: "e.g. 20, 50, 1000" },
      { key: "grain", label: "Grain weight (gr)", type: "number", hint: "e.g. 55, 124, 147" },
      { key: "shot_size", label: "Shot size", type: "select", options: AMMO_SHOT_SIZES },
      { key: "dram", label: "Dram", type: "select", options: AMMO_DRAMS },
      { key: "oz_shot", label: "Ounce of shot", type: "suggest", suggest: AMMO_OZ_SHOT },
      { key: "ca_nonlead", label: "CA Certified Nonlead", type: "bool" },
      { key: "bullet_type", label: "Bullet / load type", type: "suggest", suggest: AMMO_LOAD_TYPES },
      { key: "makeup", label: "Make-up", type: "select", options: AMMO_MAKEUP },
    ],
  },
  drones: {
    topRequired: ["brand", "model"],
    fields: [
      { key: "product_line", label: "Product line", type: "text", required: true, hint: "e.g. Mavic, Mini, Matrice, Agras" },
      { key: "class", label: "Class", type: "select", required: true, options: ["Consumer", "Enterprise", "Agricultural", "Delivery"] },
      { key: "camera_resolution", label: "Camera resolution", type: "text", hint: "e.g. 4K/60, 6K, 50MP" },
      { key: "max_flight_time", label: "Max flight time", type: "text", hint: "e.g. 46 min" },
      { key: "max_transmission_range", label: "Max transmission range", type: "text", hint: "e.g. 20 km (O4)" },
      { key: "weight", label: "Weight", type: "text", hint: "e.g. 895 g" },
      { key: "obstacle_avoidance", label: "Obstacle avoidance", type: "bool" },
      { key: "thermal_equipped", label: "Thermal-equipped", type: "bool" },
      { key: "rtk_gps", label: "RTK / GPS", type: "bool" },
      { key: "battery_type", label: "Battery type", type: "text", hint: "e.g. LiPo 4S, Intelligent Flight Battery" },
      { key: "kit_contents", label: "Kit contents", type: "text", hint: 'e.g. drone, RC, 3 batteries, charger, hub' },
    ],
  },
};

// Fields visible for the currently-selected specs (evaluates showIf). For Ammunition the field set
// is LEAF-aware (caliber options + Grain vs Shot Size + shell-only fields depend on the subcategory).
export const visibleFieldsFor = (group, specs, leaf) => {
  if (group === "ammunition") return ammoFieldsForLeaf(leaf);
  const cfg = PART_SPECS[group];
  if (!cfg) return [];
  return cfg.fields.filter((f) => !f.showIf || f.showIf(specs || {}));
};

// ---- Prohibited / moderation detection (mirror of backend part_categories.py) ----
// Barreled action → HARD BLOCK (includes serialized receiver). Lower receiver / serial number →
// route to manual review (not published immediately). We exclude the common optic phrase
// "lower 1/3 / third / co-witness" and price words so we don't false-flag legit sight listings.
const BARRELED_ACTION_RE = /\bbarreled\s*actions?\b/i;
const LOWER_PHRASE_RE = /\b(stripped\s+lowers?|complete\s+lowers?|lower\s+receivers?)\b/i;
const BARE_LOWER_RE = /\blowers?\b/i;
const LOWER_EXCLUDE_RE = /lowers?\s*(1\s*\/\s*3|1\/3rd|third|co-?witness|price|priced|cost|half|portion|third-?s)/i;
const SERIAL_RE = /(\bserial\b|\bs\/n\b|\bserial\s*(number|no\.?|#))/i;

export function detectProhibited(...parts) {
  const text = parts.filter(Boolean).join(" \n ");
  if (!text.trim()) return null;
  if (BARRELED_ACTION_RE.test(text)) {
    return { block: "A barreled action includes the receiver, which is a serialized firearm. OpticSwap does not list firearms." };
  }
  const reasons = [];
  if (LOWER_PHRASE_RE.test(text)) reasons.push("mentions a lower receiver");
  else if (BARE_LOWER_RE.test(text) && !LOWER_EXCLUDE_RE.test(text)) reasons.push("mentions \u201clower\u201d");
  if (SERIAL_RE.test(text)) reasons.push("mentions a serial number");
  if (reasons.length) {
    return { review: `This listing ${reasons.join(" and ")} — a lower receiver is a serialized firearm and parts here are not serialized, so a moderator will review it before it goes live.` };
  }
  return null;
}
